import { computed, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { AuthService } from "@auth0/auth0-angular";
import { AddressDto } from "@dtos/address-dto/address-dto";
import { CreateAddressRequest } from "@dtos/address-dto/create-address-request";
import { CustomerProfileDto } from "@dtos/customer-profile-dtos/customer-profile-dto";
import { UpdateCustomerProfileDto } from "@dtos/customer-profile-dtos/update-customer-profile-dto";
import { CreateIdentificationCardDto } from "@dtos/identification-card-dtos/create-identification-card-dto";
import { IdentificationCardDto } from "@dtos/identification-card-dtos/identification-card-dto";
import { UpdateIdentificationCardDto } from "@dtos/identification-card-dtos/update-identification-card-dto";
import { MessageBusService } from "@services/message-bus.service";
import { RequestHandlerService } from "@services/request-handler.service";
import { environment } from '@envs/environment';

@Injectable({
    providedIn: 'root'
})
export class ProfileService {
    private readonly _profilesBaseUrl = environment.apiUrl + 'CustomerProfiles/';
    private readonly _addressBaseUrl = environment.apiUrl + 'Address/';
    private readonly _valuesBaseUrl = environment.apiUrl + 'Values/';
    private readonly _idBaseUrl = environment.apiUrl + 'IdentificationCard/';

    private readonly _authService = inject(AuthService);
    private readonly _req = inject(RequestHandlerService);
    private readonly _messageBus = inject(MessageBusService);

    isLoadingCustomerProfile = signal(false);
    isLoadingIdentificationCardTypes = signal(false);
    isCreatingOrUpdatingCustomerProfile = signal(false);
    isUpdatingAddress = signal(false);
    isCreatingAddress = signal(false);
    isDeletingAddress = signal(false);
    isCreatingIdentificationCard = signal(false);
    isUpdatingIdentificationCard = signal(false);
    isDeletingIdentificationCard = signal(false);
    isAnonymous = computed(() => !this.customerProfile()?.id);

    customerProfile = signal<CustomerProfileDto | undefined>(undefined);
    identificationCardTypes = signal<string[]>([]);

    constructor() {
        this._authService.isAuthenticated$
            .pipe(takeUntilDestroyed())
            .subscribe(async (isAuthenticated) => {
                if (isAuthenticated) {
                    await this.loadCustomerProfileAsync();
                } else {
                    this.customerProfile.set(undefined);
                }
            });

        this._messageBus.onOrderAccepted
            .pipe(takeUntilDestroyed())
            .subscribe(async accepted => {
                if (!accepted) return;
                this.reset();
                await this.loadCustomerProfileAsync();
            });
    }

    async createOrUpdate(dto: UpdateCustomerProfileDto) {
        const result = await this._req.put<CustomerProfileDto>(
            this._profilesBaseUrl,
            dto,
            this.isCreatingOrUpdatingCustomerProfile,
        );
        if (result.type === 'error') return undefined;
        const profile = result.data;
        this.customerProfile.set(profile);
        return profile;
    }

    async loadCustomerProfileAsync() {
        const response = await this._req.get<CustomerProfileDto>(this._profilesBaseUrl, this.isLoadingCustomerProfile);
        if (response.type === 'success') this.customerProfile.set(response.data);
    }

    async createAddress(dto: CreateAddressRequest) {
        if (this.isAnonymous()) {
            this.customerProfile.set(this._createProfileFromAddress(dto));
            return;
        }

        const response = await this._req.post<AddressDto>(
            this._addressBaseUrl,
            dto,
            this.isCreatingAddress,
        );
        if (response.type === 'error') return;
        const address = response.data;

        this.customerProfile.update(profile => ({
            ...profile,
            addresses: this._upsertById(profile.addresses, address),
        }));

        return address;
    }

    async updateAddress(dto: CreateAddressRequest & { id?: number }) {
        if (this.isAnonymous()) {
            this.customerProfile.update(profile => {
                return {...profile, addresses: [{...dto, id: 0}]};
            });
            return;
        }

        const response = await this._req.patch<AddressDto>(
            this._addressBaseUrl,
            dto,
            this.isUpdatingAddress,
        );
        if (response.type === 'error') return;
        const address = response.data;

        this.customerProfile.update(profile => ({
            ...profile,
            addresses: this._upsertById(profile.addresses, address),
        }));

        return address;
    }

    async deleteAddress(addressId: number) {
        if (this.isAnonymous()) {
            this.customerProfile.update(profile => {
                return {...profile, addresses: []};
            });
            return;
        }

        const response = await this._req.delete(this._addressBaseUrl + addressId, this.isDeletingAddress);
        if (response.type === 'error') return false;

        this.customerProfile.update(profile => ({
            ...profile,
            addresses: profile.addresses.filter(x => x.id !== addressId),
        }));

        return true;
    }

    async loadIdentificationCardTypesAsync() {
        const response = await this._req.get<string[]>(
            this._valuesBaseUrl + 'identificationCardTypes',
            this.isLoadingIdentificationCardTypes,
        );
        if (response.type === 'error') return;
        this.identificationCardTypes.set(response.data);
    }

    async createIdentificationCard(dto: CreateIdentificationCardDto) {
        const response = await this._req.post<IdentificationCardDto>(
            this._idBaseUrl,
            dto,
            this.isCreatingIdentificationCard,
        );
        if (response.type === 'error') return undefined;
        const idCard = response.data;

        this.customerProfile.update(profile => ({
            ...profile,
            identificationCards: this._upsertById(profile.identificationCards, idCard),
        }));

        return idCard;
    }

    async deleteIdentificationCard(id: number) {
        const response = await this._req.delete(this._idBaseUrl + id, this.isDeletingIdentificationCard);
        if (response.type === 'error') return false;

        this.customerProfile.update(profile => ({
            ...profile,
            identificationCards: profile.identificationCards.filter(x => x.id !== id),
        }));

        return true;
    }

    async updateIdentificationCard(dto: UpdateIdentificationCardDto) {
        const response = await this._req.patch<IdentificationCardDto>(
            this._idBaseUrl,
            dto,
            this.isUpdatingIdentificationCard,
        );
        if (response.type === 'error') return undefined;
        const idCard = response.data;

        this.customerProfile.update(profile => ({
            ...profile,
            identificationCards: this._upsertById(profile.identificationCards, idCard),
        }));

        return idCard;
    }

    reset() {
        this.identificationCardTypes.set([]);

        this.isLoadingCustomerProfile.set(false);
        this.isLoadingIdentificationCardTypes.set(false);
        this.isCreatingOrUpdatingCustomerProfile.set(false);
        this.isUpdatingAddress.set(false);
        this.isCreatingAddress.set(false);
        this.isDeletingAddress.set(false);
        this.isCreatingIdentificationCard.set(false);
        this.isUpdatingIdentificationCard.set(false);
        this.isDeletingIdentificationCard.set(false);
    }

    private _upsertById<T extends { id: number }>(items: T[], item: T): T[] {
        const index = items.findIndex(x => x.id === item.id);
        if (index === -1) return [...items, item];
        return items.map((x, i) => i === index ? item : x);
    }

    private _createProfileFromAddress(address: CreateAddressRequest): CustomerProfileDto {
        return {
            id: 0,
            customerName: address.contactName,
            phoneNumber: address.phoneNumber,
            addresses: [{
                phoneNumber: address.phoneNumber,
                email: address.email,
                city: address.city,
                contactName: address.contactName,
                addressName: address.addressName,
                fullAddress: address.fullAddress,
                lat: address.lat,
                lng: address.lng,
                id: 0,
            }],
            identificationCards: [],
        }
    }
}
